import {observe} from '@github-ui/selector-observer'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

// Markup contract with the server-rendered artifact reference placeholders.
export const ISSUE_ARTIFACT_NAME = {
  selector: '.js-issue-artifact-name',
  issueIdAttribute: 'data-artifact-issue-id',
  numberAttribute: 'data-artifact-number',
  fallbackText: (number: number) => `(artifact ${number})`,
} as const

type ArtifactNames = Map<number, string>
// `null` marks a temporary failure (network error, 429 or 5xx) that a later placeholder may retry.
type ArtifactNamesRequest = Promise<ArtifactNames | null>
// Returned for 4xx responses, which won't succeed on retry.
const UNAVAILABLE: ArtifactNames = new Map()

// Placeholders for the same issue share one in-flight request, so each element can be handled on its own.
const requestsByIssueId = new Map<string, ArtifactNamesRequest>()
// Numbers already refetched once, so a missing or deleted artifact can't trigger repeated requests.
const refetchedNumbersByIssueId = new Map<string, Set<number>>()

export function resetIssueArtifactNameCache() {
  requestsByIssueId.clear()
  refetchedNumbersByIssueId.clear()
}

function readPlaceholder(el: Element): {issueId: string; number: number} | null {
  const issueId = el.getAttribute(ISSUE_ARTIFACT_NAME.issueIdAttribute) ?? ''
  const number = Number(el.getAttribute(ISSUE_ARTIFACT_NAME.numberAttribute))
  if (!/^\d+$/.test(issueId) || !Number.isInteger(number) || number <= 0) return null
  return {issueId, number}
}

async function fetchArtifactNames(issueId: string): ArtifactNamesRequest {
  const names: ArtifactNames = new Map()
  try {
    const params = new URLSearchParams({
      resource_type: 'issue',
      resource_id: issueId,
      per_page: '100',
    })
    const response = await verifiedFetchJSON(`/scribe/artifacts/names?${params.toString()}`)
    if (response.status === 429) {
      // Keep this request cached until the rate limit clears, so later placeholders don't retry too early.
      await new Promise(resolve => setTimeout(resolve, (Number(response.headers.get('Retry-After')) || 0) * 1000))
      return null
    }
    if (response.status >= 500) return null
    if (!response.ok) return UNAVAILABLE

    const payload: unknown = await response.json()
    const artifacts = Array.isArray(payload) ? payload : (payload as {artifacts?: unknown})?.artifacts
    if (!Array.isArray(artifacts)) return UNAVAILABLE

    for (const artifact of artifacts) {
      const {number, name} = (artifact ?? {}) as {number?: unknown; name?: unknown}
      if (typeof number === 'number' && typeof name === 'string' && name.trim() !== '') {
        names.set(number, name)
      }
    }
  } catch {
    return null
  }
  return names
}

async function artifactNamesFor(issueId: string) {
  let request = requestsByIssueId.get(issueId)
  if (!request) {
    request = fetchArtifactNames(issueId)
    requestsByIssueId.set(issueId, request)
  }
  const names = await request
  if (!names && requestsByIssueId.get(issueId) === request) requestsByIssueId.delete(issueId)
  return {names, request}
}

function markRefetched(issueId: string, number: number): boolean {
  const refetched = refetchedNumbersByIssueId.get(issueId) ?? new Set<number>()
  refetchedNumbersByIssueId.set(issueId, refetched)
  if (refetched.has(number)) return false
  refetched.add(number)
  return true
}

async function nameFor(issueId: string, number: number): Promise<string | undefined> {
  const first = await artifactNamesFor(issueId)
  const name = first.names?.get(number)
  if (name || !first.names || first.names === UNAVAILABLE) return name

  // The number may belong to an artifact created after the names were fetched.
  if (markRefetched(issueId, number) && requestsByIssueId.get(issueId) === first.request) {
    requestsByIssueId.delete(issueId)
  }
  if (requestsByIssueId.get(issueId) === first.request) return undefined
  return (await artifactNamesFor(issueId)).names?.get(number)
}

async function fillArtifactName(el: Element) {
  const placeholder = readPlaceholder(el)
  if (!placeholder) return

  const name = await nameFor(placeholder.issueId, placeholder.number)
  if (!name || !el.isConnected) return
  if (el.textContent?.trim() !== ISSUE_ARTIFACT_NAME.fallbackText(placeholder.number)) return
  el.textContent = `(${name})`
}

observe(ISSUE_ARTIFACT_NAME.selector, {add: el => void fillArtifactName(el)})
